import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, Router, UrlSegment } from "@angular/router";
import { environment } from "@environments/environment";
import Keycloak from "keycloak-js";
import { catchError, EMPTY, map, Observable, of, switchMap } from "rxjs";
import { AuthService } from "../interfaces/IAuthService";
import { MatDialog } from "@angular/material/dialog";

const AUTHORIZATION = {
    basicPrefix: "Basic",
    bearerPrefix: "Bearer",
    headerName: "Authorization",
};

type beforeFunc = (
    request: HttpRequest<unknown>,
) => Observable<HttpRequest<unknown>>;
type prepareFunc = (request: HttpRequest<unknown>) => Promise<HttpRequest<unknown>>;
type onErrorFunc = (
    error: HttpRequest<unknown>,
    caught: Observable<HttpEvent<any>>,
    request: HttpRequest<unknown>,
    next: HttpHandler,
) => Observable<HttpEvent<any>>;
type afterFunc = (event: HttpEvent<unknown>) => Observable<HttpEvent<unknown>>;

interface Handlers {
    before?: beforeFunc;
    prepareRequest?: prepareFunc;
    onError?: Record<number, onErrorFunc>;
    after?: afterFunc;
}

type EndpointsHandlers = Record<string, Handlers>;

const BLACK_LIST_ORIGIN_REDIRECT: string[] = [
    "backend-not-available",
    "/backend-not-available",
];

const BLACK_LIST_ORIGIN_ACCOUNT_DISABLED: string[] = [
    "account-deactivated",
    "/account-deactivated",
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly authService = inject(AuthService);
    private readonly keycloakService = inject(Keycloak);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly matDialog = inject(MatDialog);

    intercept(
        request: HttpRequest<unknown>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        if (request.url.startsWith(environment.api.graphqlUrl)
            || request.url.startsWith(environment.api.restUrl)
            || request.url.startsWith(environment.api.wsGraphqlURL)
            || request.url.startsWith(environment.api.downloadEndpoint)
            || request.url.startsWith(environment.api.resourceFileUrl)
            || request.url.startsWith(environment.api.whiteBoardUrl)
            || request.url.startsWith(environment.api.trackLink)
            || request.url.startsWith(environment.api.whiteBoardUrls)
            || request.url.startsWith(environment.keycloak.url)
        ) {
            return of(this.keycloakService.token).pipe(
                map((token) => {
                    if (token == null) {
                        throw new Error("Token is null");
                    }

                    return token;
                }),
                map(accessToken => this.prepareAuthenticatedRequest(request, accessToken)),
                switchMap(req => next.handle(req)),
                catchError((err) => {
                    const pathOrigin = this.getCurrentRoute();
                    if (err.status === 401 || err.message === "Token is null") {
                        this.authService.logOut();
                    }
                    if (err.status === 403) {
                        this.matDialog.closeAll();
                        if (err.error?.reason === "ACCOUNT_DISABLED") {
                            if (!BLACK_LIST_ORIGIN_ACCOUNT_DISABLED.includes(pathOrigin)) {
                                this.router.navigate(["/account-deactivated"]);
                            }
                        } else {
                            this.router.navigate(["/not-authorized-app-access"]);
                        }
                    } else if (err.status === 0 && !BLACK_LIST_ORIGIN_REDIRECT.includes(pathOrigin)) {
                        this.matDialog.closeAll();
                        this.router.navigate(["/backend-not-available"], { queryParams: { pathOrigin } });
                        console.error(err);
                        throw new Error(`Loose connection with backend [${err.status}]`);
                    }
                    throw err;
                }),
            );
        } else {
            return next.handle(request);
        }
    }

    private getCurrentRoute(): string {
        try {
            let route = this.route;
            const paths: UrlSegment[] = [];
            paths.push(...route.snapshot.url);
            while (route.firstChild) {
                route = route.firstChild;
                paths.push(...route.snapshot.url);
            }
            return "/" + paths.join("/");
        } catch (e) {
            console.warn("Running a query while route is null", e);
            return "/";
        }
    }

    // Before Functions

    private prepareAuthenticatedRequest(
        req: HttpRequest<unknown>,
        accessToken: string,
    ): HttpRequest<unknown> {
        const token = `${AUTHORIZATION.bearerPrefix} ${accessToken}`;
        return req.clone({
            headers: req.headers.append(AUTHORIZATION.headerName, token),
        });
    }

    // onError Functions

    private logout(_caught: Observable<unknown>) {
        this.authService.logOut();
        console.warn("401 - disconnected");
        return EMPTY;
    }

    // After functions
}
